import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Users, Building2, Handshake, FolderKanban, TrendingUp, Trophy, CheckSquare, Wallet,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip,
} from "recharts";
import api from "../api";
import { useAuth } from "../auth";
import AICommandBar from "../components/AICommandBar";
import { Spinner } from "../components/common";
import { formatMoney, formatMoneyByCurrency } from "../format";
import { useBranding } from "../context/BrandingContext";
import { useChartPalette } from "../components/Brand";

const STAGE_COLORS = {
  lead: "#64748b", qualified: "#3b82f6", proposal: "#8b5cf6",
  negotiation: "#f59e0b", won: "#10b981", lost: "#ef4444",
};

function Metric({ icon: Icon, label, value, accent, metricKey }) {
  return (
    <div className="border border-border rounded-sm p-5 bg-surface-soft hover:-translate-y-px transition-transform duration-200" data-testid={`metric-${metricKey}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted">{label}</span>
        <Icon size={16} className={accent} />
      </div>
      <div className="font-display text-3xl font-bold mt-3 tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const chartPalette = useChartPalette();
  const { branding } = useBranding();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  // null = auto-pick whichever currency actually has data once stats loads;
  // becomes a fixed choice once the user clicks the toggle.
  const [chartCurrency, setChartCurrency] = useState(null);

  const load = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/activities", { params: { completed: "false", upcoming: "true" } })
      .then((r) => setTasks(r.data.slice(0, 5)));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!stats) return <Spinner />;

  const fmt = (n) => new Intl.NumberFormat().format(n);
  const money = formatMoney;
  const moneyByCurrency = formatMoneyByCurrency;

  // Which of this instance's currencies actually carry pipeline value. The
  // toggle used to be a hardcoded EUR/HUF pair shown only when HUF had data;
  // now it lists exactly the currencies with something to show, so an
  // instance configured for one currency gets no toggle and one configured
  // for five gets five buttons.
  const currenciesWithData = branding.currencies.filter(
    (c) => stats.deals_by_stage.some((d) => d.value_by_currency?.[c])
  );
  const effectiveCurrency =
    (chartCurrency && currenciesWithData.includes(chartCurrency) && chartCurrency) ||
    currenciesWithData[0] ||
    branding.default_currency;
  const stageData = stats.deals_by_stage.map((d) => ({
    name: t(`statuses.${d.stage}`), value: d.value_by_currency?.[effectiveCurrency] ?? null, stage: d.stage,
  }));
  const statusData = stats.contacts_by_status.map((d) => ({
    name: t(`statuses.${d.status}`), value: d.count,
  }));

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted">{t("dashboard.greeting")}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{user?.name}</h1>
      </div>

      <AICommandBar onResult={load} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="dashboard-metrics">
        <Metric metricKey="contacts" icon={Users} label={t("dashboard.contacts")} value={fmt(stats.total_contacts)} accent="text-primary" />
        <Metric metricKey="companies" icon={Building2} label={t("dashboard.companies")} value={fmt(stats.total_companies)} accent="text-glow" />
        <Metric metricKey="openDeals" icon={Handshake} label={t("dashboard.openDeals")} value={fmt(stats.open_deals)} accent="text-amber-500" />
        <Metric metricKey="activeProjects" icon={FolderKanban} label={t("dashboard.activeProjects")} value={fmt(stats.active_projects)} accent="text-success" />
        <Metric metricKey="pipelineValue" icon={TrendingUp} label={t("dashboard.pipelineValue")} value={moneyByCurrency(stats.pipeline_value)} accent="text-primary" />
        <Metric metricKey="wonValue" icon={Trophy} label={t("dashboard.wonValue")} value={moneyByCurrency(stats.won_value)} accent="text-success" />
        <Metric metricKey="cashFlow" icon={Wallet} label={t("dashboard.cashFlow")} value={moneyByCurrency(stats.cash_flow_by_currency)} accent="text-glow" />
        <Metric metricKey="openTasks" icon={CheckSquare} label={t("dashboard.openTasks")} value={fmt(stats.open_tasks)} accent="text-amber-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border rounded-sm p-5" data-testid="dashboard-pipeline-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold">{t("dashboard.pipelineByStage")}</h3>
            {currenciesWithData.length > 1 && (
              <div className="flex border border-border rounded-sm overflow-hidden text-xs">
                {currenciesWithData.map((c) => (
                  <button key={c} onClick={() => setChartCurrency(c)} data-testid={`chart-currency-${c}`}
                    className={`px-2 py-1 transition-colors ${effectiveCurrency === c ? "bg-primary text-white" : "text-muted hover:bg-surface"}`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stageData}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted)" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "var(--muted)" }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: "var(--surface)" }}
                contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 3, fontSize: 12 }}
                formatter={(v) => money(v, effectiveCurrency)}
              />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {stageData.map((d, i) => (
                  <Cell key={i} fill={STAGE_COLORS[d.stage] || branding.primary} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border rounded-sm p-5">
          <h3 className="font-display font-bold mb-4">{t("dashboard.contactsByStatus")}</h3>
          {statusData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {statusData.map((d, i) => <Cell key={i} fill={chartPalette[i % chartPalette.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 3, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted">{t("common.noResults")}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {statusData.map((d, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs text-muted">
                <span className="w-2 h-2 rounded-full" style={{ background: chartPalette[i % chartPalette.length] }} />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-border rounded-sm p-5" data-testid="dashboard-upcoming-tasks">
        <h3 className="font-display font-bold mb-4">{t("dashboard.upcomingTasks")}</h3>
        {tasks.length ? (
          <div className="divide-y divide-border">
            {tasks.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-3" data-testid={`dash-task-${a.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                  <span className="text-sm truncate">{a.subject}</span>
                </div>
                <span className="text-xs text-muted shrink-0 ml-2">
                  {a.due_date ? new Date(a.due_date).toLocaleDateString() : "—"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted">{t("common.noResults")}</p>
        )}
      </div>
    </div>
  );
}
